import type { FC, SyntheticEvent } from "react";
import { useMemo, useCallback } from "react";

import { DotsLoader, ListItemSeparator, type IAutocompleteProps } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import type { LobItem } from "app/components/compare/WebsiteQueryBar/useLobSites";
import { translate } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";

import { NoData } from "./components/NoData";
import { ScrollAreaWrap } from "./components/ScrollAreaWrapper";
import { MainSiteAutocompleteStyled, ListItemWebsiteStyled } from "./styles";

interface IAutocompleteWithLineOfBusinessProps {
    lobSites: LobItem[];
    topAnalyzedLOBWebsites: LobItem[];
    isLoading: boolean;
    onClick: (item: LobItem) => void;
    similarLobSites?: LobItem[];
    className?: string;
    onClose?: (e?: any) => void;
    overrideEnterFunc?: (e: SyntheticEvent, item?: unknown) => void;
    autocompleteProps?: any;
    excludes?: any[];
}

export const AutocompleteWithLineOfBusiness: FC<IAutocompleteWithLineOfBusinessProps> = ({
    className,
    onClose,
    overrideEnterFunc,
    autocompleteProps = {},
    lobSites = [],
    isLoading,
    excludes = [],
    onClick,
    topAnalyzedLOBWebsites = [],
    similarLobSites = [],
}) => {
    const excludedNames = useMemo(() => new Set(excludes.map((x) => x.name.toLowerCase())), [excludes]);

    const filterExcluded = useCallback(
        (sites: LobItem[]) => sites.filter((s) => !excludedNames.has(s.name.toLowerCase())),
        [excludedNames],
    );

    const getListItems = useCallback(
        (query = "") => {
            const searchTerm = query.trim().toLowerCase();
            const isSearching = searchTerm.length > 0;

            const filteredSimilar = filterExcluded(similarLobSites);
            const filteredTop = filterExcluded(topAnalyzedLOBWebsites);
            const filteredLobSites = filterExcluded(lobSites);

            const list = isSearching
                ? filteredLobSites.filter((s) => s.name.toLowerCase().includes(searchTerm))
                : filteredSimilar.length > 0
                  ? filteredSimilar
                  : filteredTop;

            return isLoading || list.length === 0 ? [] : list;
        },
        [isLoading, lobSites, similarLobSites, topAnalyzedLOBWebsites, filterExcluded],
    );

    const dynamicTitle = useMemo(() => {
        const filteredSimilar = filterExcluded(similarLobSites);
        return filteredSimilar.length > 0
            ? "analysis.content.lob.autocomplete.similar.sites.title"
            : "analysis.content.lob.autocomplete.title";
    }, [similarLobSites, filterExcluded]);

    const onEnter = overrideEnterFunc;

    const renderAutocompleteItems: IAutocompleteProps["renderItems"] = useCallback(
        ({ listItems, selectedItemId, query = "" }) => {
            const searchTerm = query.trim().toLowerCase();
            const isSearching = searchTerm.length > 0;
            const sectionKey = isSearching ? `search-${searchTerm}` : "default";

            if (!listItems.length) {
                return (
                    <div className="ListItemsContainer">
                        <NoData text={translate("analysis.content.lob.autocomplete.no.data")} />
                    </div>
                );
            }

            return (
                <div className="ListItemsContainer">
                    <Stack gap="8px">
                        <section key={sectionKey}>
                            {!isSearching ? <ListItemSeparator>{translate(dynamicTitle)}</ListItemSeparator> : null}
                            <ScrollAreaWrap>
                                {listItems.map((site: LobItem, idx: number) => {
                                    const img = AssetsService.getFavIconPath(site.icon);
                                    return (
                                        <ListItemWebsiteStyled
                                            key={`${site.name}-${idx}`}
                                            isActive={idx === selectedItemId}
                                            img={img}
                                            text={site.name}
                                            onClick={() => onClick({ ...site, icon: img })}
                                        />
                                    );
                                })}
                            </ScrollAreaWrap>
                        </section>
                    </Stack>
                </div>
            );
        },
        [onClick, dynamicTitle],
    );

    return (
        <MainSiteAutocompleteStyled
            className={className}
            isFocused
            isLoading={isLoading}
            autoFocus
            loadingComponent={<DotsLoader />}
            preventTruncateUnlessForced
            resetValueOnSelect
            floating
            debounce={250}
            maxResults={20}
            onCloseClick={onClose}
            getListItems={getListItems}
            renderItems={renderAutocompleteItems}
            placeholder={translate("analysis.content.lob.autocomplete.placeholder")}
            overrideEnterFunc={onEnter}
            initialSelectedItemId={-1}
            {...autocompleteProps}
        />
    );
};
